/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HealthMonitorModule
 */

import {
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import {
    HealthMonitor,
    HealthMonitorLdap,
    HealthMonitorSSLAttributes,
} from 'object-types';

import {
    CertificateCollection,
} from 'ajs/modules/security/factories/certificates/certificate.collection.factory';

import {
    PKIProfileCollection,
} from 'ajs/modules/security/factories/pki-profile/pki-profile.collection.factory';

import {
    SSLProfileCollection,
} from 'ajs/modules/security/factories/ssl-profile/ssl-profile.collection.factory';

import type {
    HealthMonitorItem,
} from 'ajs/modules/healthmonitor/factories/health-monitor-item.factory';

import type {
    HealthMonitorLdapConfigItem,
} from 'ajs/modules/healthmonitor/factories/health-monitor-ldap.config-item.factory';

import * as globalL10n from 'global-l10n';
import * as l10n from './ldap-health-monitor-config.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * CertificateCollection Type.
 */
type TCertificateCollection = typeof CertificateCollection;

/**
 * PKIProfileCollection Type.
 */
type TPkiProfileCollection = typeof PKIProfileCollection;

/**
 * SSLProfileCollection Type.
 */
type TSslProfileCollection = typeof SSLProfileCollection;

/**
 * @description Component to display the LDAP/LDAPS section in the Health Monitor modal.
 * @author Sarthak Kapoor
 */
@Component({
    selector: 'ldap-health-monitor-config',
    templateUrl: './ldap-health-monitor-config.component.html',
})
export class LdapHealthMonitorConfigComponent implements OnChanges, OnInit, OnDestroy {
    @Input()
    public editable: HealthMonitorItem;

    /**
     * Passed as a param to ssl, pki and certificate profile collections.
     */
    @Input()
    public isFederated = false;

    /**
     * Health Monitor config type. This can be either ldap_monitor or ldaps_monitor.
     */
    public ldapConfigItem: HealthMonitorLdapConfigItem;

    /**
     * Flag to show fields applicable only for LDAPS type.
     */
    public isLdapsType = false;

    public readonly certificateCollection: CertificateCollection;

    public readonly pkiProfileCollection: PKIProfileCollection;

    public readonly sslProfileCollection: SSLProfileCollection;

    public readonly objectType = HealthMonitorLdap;

    public readonly objectTypes = {
        HealthMonitor,
        HealthMonitorLdap,
        HealthMonitorSSLAttributes,
    };

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    constructor(
        l10nService: L10nService,
        @Inject(CertificateCollection)
        CertificateCollection: TCertificateCollection,
        @Inject(PKIProfileCollection)
        PKIProfileCollection: TPkiProfileCollection,
        @Inject(SSLProfileCollection)
        SSLProfileCollection: TSslProfileCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.certificateCollection = new CertificateCollection();
        this.pkiProfileCollection = new PKIProfileCollection();
        this.sslProfileCollection = new SSLProfileCollection();
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { isFederated } = changes;
        const params = {
            is_federated: isFederated?.currentValue,
        };

        this.sslProfileCollection.setParams(params);
        this.pkiProfileCollection.setParams(params);
        this.certificateCollection.setParams(params);
    }

    /** @override */
    public ngOnInit(): void {
        if (this.editable.isLdapsType()) {
            this.ldapConfigItem = this.editable.config.ldaps_monitor;
            this.isLdapsType = true;
        } else {
            this.ldapConfigItem = this.editable.config.ldap_monitor;
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.sslProfileCollection.destroy();
        this.pkiProfileCollection.destroy();
        this.certificateCollection.destroy();
    }
}
